import React from 'react'
import clsx from 'clsx'
import Link from 'next/link'
import { Route } from 'next'

const baseClassName = clsx(
  'font-semibold text-button relative isolate inline-flex items-center justify-center text-center',
  'cursor-button data-[disabled]:cursor-default transition-all',
  'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-button1',
  'focus:outline-none focus-visible:outline-none',
)

const primaryColor = clsx(
  'bg-button1 text-surface',
  'hover:bg-button-fill-hover',
  'active:bg-button-fill-hover active:text-button-fill-inactive',
)
const secondaryColor = clsx(
  'bg-card-background text-button1',
  'hover:bg-background',
  'active:bg-background active:text-button-fill-hover',
  'border border-2 border-border',
)

const disabledPrimary = 'bg-button-fill-inactive text-surface cursor-default'
const disabledSecondary = 'bg-button-secondary text-button-fill-inactive border border-2 border-border cursor-default'

const navigationColor = 'bg-border text-primary hover:bg-grey4 active:bg-grey3'
const disabledNavigationColor = 'bg-border text-grey3 cursor-default'

const largeIconAndTextPadding = 'pl-4 pr-6 py-4 gap-2 rounded-[28px]'
const largeIconOnlyPadding = 'p-4 rounded-full'
const largeTextOnlyPadding = 'py-4 px-6 rounded-[28px]'

const smallIconAndTextPadding = 'pl-4 pr-3 py-2 gap-1 rounded-[24px]'
const smallIconOnlyPadding = 'p-3 rounded-full'
const smallTextOnlyPadding = 'py-2 px-4 rounded-[24px]'

export type LandingButtonProps<T extends string> = (
  | (Omit<React.ComponentProps<'a'>, 'href'> & {
      href: Route<T>
    })
  | React.ComponentProps<'button'>
) & {
  large?: boolean
  secondary?: boolean
  navigation?: boolean
  icon?: React.ReactNode
  shadow?: boolean
}

export const LandingButton = function LandingButton({
  large,
  secondary,
  navigation,
  className: providedClassName,
  icon,
  children,
  shadow,
  ...props
}: LandingButtonProps<string>) {
  const hasIcon = React.useMemo(() => icon !== undefined, [icon])
  const hasText = React.useMemo(() => children !== undefined, [children])
  const inactive = React.useMemo(() => 'disabled' in props && !!props.disabled, [props])

  const padding = React.useMemo(() => {
    if (navigation) return smallIconOnlyPadding
    if (large && hasIcon && hasText) return largeIconAndTextPadding
    if (large && hasIcon) return largeIconOnlyPadding
    if (large && hasText) return largeTextOnlyPadding
    if (hasIcon && hasText) return smallIconAndTextPadding
    if (hasIcon) return smallIconOnlyPadding
    if (hasText) return smallTextOnlyPadding
    return ''
  }, [large, hasIcon, hasText, navigation])

  const colors = React.useMemo(() => {
    if (navigation && inactive) return disabledNavigationColor
    if (navigation) return navigationColor
    if (secondary && inactive) return disabledSecondary
    if (secondary) return secondaryColor
    if (inactive) return disabledPrimary
    return primaryColor
  }, [navigation, secondary, inactive])

  const className = clsx(baseClassName, padding, colors, shadow && 'shadow-overlap', providedClassName)
  const inner = (
    <>
      {hasIcon && large && icon}
      {children}
      {hasIcon && !large && icon}
    </>
  )

  return 'href' in props && props.href ? (
    <Link className={className} {...props} href={(inactive ? undefined : props.href) as Route<any>}>
      {inner}
    </Link>
  ) : (
    <button className={className} disabled={inactive} {...(props as any)}>
      {inner}
    </button>
  )
} as <T extends string>(
  props: LandingButtonProps<T> & { ref?: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement> },
) => React.ReactElement<any>
